/**
 * Total dissolved gas (HYDRAULICS §7).
 *
 *     TDG_spill = min(cap, base + slope·q_gate)     q_gate = Q_gate / L  [m³/s per m of width]
 *     TDG_mix   = Σ(Q_i · TDG_i) / Σ Q_i            turbine 101 %, LLO 125 %
 *
 * Then first-order degassing downstream with a 12 h e-folding toward 100 %.
 *
 * This saturates fast — one gate passing 157 m³/s already produces 110 %, the licence limit.
 * Spilling and staying compliant are close to mutually exclusive at Kettle Narrows, which is the
 * intended dilemma and why the flood-operations waiver matters.
 *
 * The model is a fitted surrogate for a plunge-pool gas-transfer process that really depends on
 * plunge depth, tailwater depth, deflectors and barometric pressure. It is kept behind this
 * interface so a better one can replace it without touching the reservoir code.
 */

import { FLOW_EPS, clamp } from './constants';
import type { SiteConfig } from './types';

export interface TdgModel {
  /** TDG of one gate's discharge, % saturation. */
  spillTdg(qGate: number, gateWidth: number): number;
  /** Flow-weighted tailrace mix, % saturation. */
  mix(components: { q: number; tdg: number }[]): number;
  /** Degas one step toward 100 %. */
  degas(tdg: number, dtS: number): number;
}

export function makeTdgModel(site: SiteConfig): TdgModel {
  const cfg = site.tdg;
  return {
    spillTdg(qGate, gateWidth) {
      if (qGate <= FLOW_EPS || gateWidth <= 0) return 100;
      const perMetre = qGate / gateWidth;
      return clamp(cfg.base + cfg.slope * perMetre, 100, cfg.cap);
    },
    mix(components) {
      let num = 0;
      let den = 0;
      for (const c of components) {
        if (c.q <= FLOW_EPS) continue;
        num += c.q * c.tdg;
        den += c.q;
      }
      if (den <= FLOW_EPS) return 100;
      return clamp(num / den, 100, cfg.cap);
    },
    degas(tdg, dtS) {
      const k = Math.exp(-dtS / (cfg.degasTauH * 3600));
      return 100 + (tdg - 100) * k;
    },
  };
}
